/**
 * Copy for the `/pros` landing page.
 *
 * Content is derived from the Vendor Onboarding "Welcome" and "Training"
 * screens in Figma (node 4002-4596 / 4005-9130), adapted from an onboarding
 * wizard into a public marketing landing page.
 */

/** Bucket the page's assets live in. Serves them exactly as uploaded: no
 *  format negotiation, no resizing, and — being bare S3 — no `Cache-Control`
 *  at all. */
export const PRO_ASSETS_BUCKET =
  'https://belong-consumer-assets.s3.us-west-1.amazonaws.com';

const PRO_ASSETS = `${PRO_ASSETS_BUCKET}/pro-services/pros`;

/** The same bucket through imgix, which fronts this prefix. Worth knowing that
 *  it does: every raster asset on the page is currently served straight from
 *  S3 at upload size and format, and the photo sets below the fold would all
 *  shrink several-fold by coming through here too. Only the logo does today,
 *  because only the logo is on the critical path. Not the video (imgix does
 *  not serve mp4) and not the OG image (social crawlers are a worse place to
 *  discover a surprise). */
const PRO_ASSETS_IMGIX = 'https://belonghome.imgix.net/pro-services/pros';

/** The imgix origin for the bucket above. Exported for `pros.images.ts`, which
 *  rewrites the page's photo URLs through it at the size they are shown. */
export const PRO_ASSETS_IMGIX_ORIGIN = 'https://belonghome.imgix.net';

/** Origin the hero logo is fetched from, preconnected in the page head so the
 *  element the page's LCP is measured on is not paying for DNS and TLS before
 *  its fetch can start. */
export const PROS_LOGO_ORIGIN = 'https://belonghome.imgix.net';

export const PROS_VIDEO_URL = `${PRO_ASSETS}/vender_onboarding_video_final.mp4`;

export const PROS_TRUSTPILOT_ICON_URL = `${PRO_ASSETS}/trustpilot_ic.svg`;

/** Photos that roll vertically beside the standards. */
/** Photos that cross-fade beside the standards. `02` is deliberately absent:
 *  it belongs to the Inspectors role below, and a photo that reads as one
 *  role's portrait shouldn't also be part of an anonymous rotation. Changing
 *  the count means retiming `standardsFade` in the CSS module. */
export const PROS_STANDARDS_IMAGES = [1, 3, 4, 5, 6, 7].map(
  (index) => `${PRO_ASSETS}/lp_pro_real_img_0${index}.jpg`,
);

/**
 * The hero logo, and the element LCP lands on.
 *
 * Through imgix rather than straight from the bucket. The source is a 620x681
 * PNG of 253 KB shown at 185px tall on a phone, which on a throttled mobile
 * connection was 6.6s of pure download — two thirds of the page's LCP once the
 * hero became eligible to be measured at all. `auto=format,compress` with
 * `h=554` (twice the 277px desktop height, so it stays sharp at DPR 2) serves
 * 25 KB of AVIF, a 98 KB PNG to anything without AVIF or WebP, and a year of
 * `Cache-Control` that bare S3 was not sending.
 *
 * Deliberately an absolute URL: that keeps `Image` on its external branch, a
 * plain eager `<img>`. Its internal branch would route through imgix too, but
 * it does so with a lazysizes placeholder that needs JavaScript to swap in the
 * real image — and making the hero wait for JavaScript again is the whole
 * thing this page was just fixed for.
 */
export const PROS_LOGO_URL = `${PRO_ASSETS_IMGIX}/vendor_logo.png?auto=format,compress&h=554`;

/** Share image for social cards: a purpose-made 1200x630 asset, replacing the
 *  page photo that stood in for it. Link previews crop hard, so the framing
 *  has to be built for that ratio rather than trusted to survive it. */
/** Copy for the lead modal every Apply button opens. It is the same form the
 *  homeowner landing page uses, opened through the shared modal manager via
 *  the `activeModal` query param. */
export const PROS_APPLY_MODAL = {
  title: 'Join the Belong Pro Network',
  subtitle:
    'Tell us about your work and we will reach out with next steps for getting onboarded.',
  /** Still required by the shared form's props, but nothing reads it while
   *  submitting is unwired: the Pro form does not post to the lead API. It
   *  will be platform's endpoint that decides how an application is filed.
   *  TODO(EXP-88). */
  source: 'AskedAQuestion',
  /** Asked first: which of the three roles someone is applying for. */
  /** `label` is what the applicant reads in the dropdown, where the
   *  parenthetical earns its place; `shortLabel` is what Slack shows, where it
   *  is just noise in the message header. */
  positions: [
    { label: 'Pro (trades and maintenance)', shortLabel: 'Pro', value: 'pro' },
    { label: 'Tour Host', shortLabel: 'Tour Host', value: 'tour-host' },
    { label: 'Inspector', shortLabel: 'Inspector', value: 'inspector' },
  ],
  /** Trades and inspection businesses often apply as a company rather than as
   *  a person; Tour Hosts apply as individuals. */
  companyPositions: ['pro', 'inspector'],
} as const;

export const PROS_OG_IMAGE_URL = `${PRO_ASSETS}/pros_og_img.jpg`;

export const PROS_URL = 'https://belonghome.com/pros';

/** The Spanish page's own indexable URL — the locale leads the path, so each
 *  locale is a page Google can crawl and rank and the pattern extends to the
 *  next translated page. Keep in step with `PROS_ROUTES` in
 *  `pros-language.tsx`. */
export const PROS_URL_ES = 'https://belonghome.com/es/pros';

export const PROS_STRINGS = {
  seo: {
    title: 'Belong Pro Network | Steady Work For Home Service Pros',
    description:
      "As a Belong Pro, you'll gain access to dozens of jobs a month through a network of rental homes people love. Flexible scheduling, fast payments, no chasing invoices.",
    /** Alt text on the share image, read by screen readers on social posts. */
    imageAlt: 'A Belong Pro at work on a home',
  },
  hero: {
    logoAlt: 'Belong Pro Network',
    headingLine1: 'WELCOME TO THE',
    headingLine2: 'BELONG PRO NETWORK.',
    description:
      "As a Belong Pro, you'll gain access to dozens of jobs a month through a network of rental homes people love.",
    cta: 'Apply',
  },
  benefits: {
    label: 'WHY PROS JOIN',
    heading: 'Work you can build a business on.',
    // Titles carry an explicit newline so every card reads as two lines in the
    // phone-width 2×2 grid. Rendered with `whitespace-pre-line`, which the
    // component switches to `whitespace-normal` from `md` up — there the
    // newline collapses to a space and each title sits on one line.
    items: [
      {
        title: 'Flexible\nScheduling',
        description:
          'Claim the jobs that fit your week. You decide what you take on and when you take it on.',
      },
      {
        title: 'Steady\nIncome',
        description:
          'Dozens of jobs a month across a growing network of homes, so your calendar stays full.',
      },
      {
        title: 'Fast\nPayments',
        description:
          'Submit your work and get paid quickly. No chasing invoices, no waiting on homeowners.',
      },
      {
        title: 'Recognition &\nHigher Rates',
        description:
          'Top-performing Pros get more jobs, higher rates, and priority scheduling. Your work is seen and rewarded.',
      },
    ],
  },
  video: {
    heading: 'What does it mean to be a Belong Pro?',
  },
  standards: {
    /** Rendered as two fixed lines, so each sentence gets its own. */
    quoteLines: [
      'You are not just completing a service.',
      'You are shaping the experience of someone’s home.',
    ],
    author: 'Tamara Epelbaum',
    authorRole: 'Head of Global Strategic Operations & Service Delivery',
    /** Eyebrow above the rolling standards, not a heading. */
    itemsLabel: 'OUR STANDARDS',
    /** Carried by the first photo of the cross-fading set; the rest are the
     *  same subject, so they go unlabelled. */
    imageAlt: 'Belong Pros at work in homes across the network',
    items: [
      'Be on time',
      'Be respectful',
      'Communicate clearly',
      'Do excellent work',
    ],
  },
  /** The three local roles behind every Belong home.
   *
   *  Presented as three parallel roles, which is how they are hired and
   *  searched for today. Tour Host and Inspector are merging internally into
   *  a single "Local Specialist" role, so if that lands, these two collapse
   *  into one entry rather than being reworded. */
  localNetwork: {
    label: 'OUR LOCAL NETWORK',
    /** Deliberately not "Belong Local Network": the eyebrow directly above it
     *  already says that, and the heading is the line worth reading. */
    heading: 'The people who show up.',
    description:
      'Three roles keep every Belong home running, shown, and cared for, in person, in every market.',
    /** Each `summary` carries an explicit break so all three sit on two lines
     *  and the descriptions below them start on the same baseline. */
    roles: [
      {
        title: 'Pros',
        imageSrc: `${PRO_ASSETS}/lp_pro_real_img_08.jpg`,
        imageAlt: 'A Belong Pro at work inside a home',
        summary: 'They keep the home\nin great shape.',
        description:
          'Licensed, vetted trade professionals who do the work themselves, never passed down a subcontracting chain. Every Pro is rated after every visit, so the best ones get more work.',
        stats: [
          { label: 'Network', value: '2,000+ Pros' },
          { label: 'Paid out', value: '$6.7M+' },
          { label: 'Rating', value: '4.8 out of 5' },
        ],
      },
      {
        title: 'Tour Hosts',
        imageSrc: `${PRO_ASSETS}/lp_pro_real_img_09.jpg`,
        imageAlt: 'A Belong Tour Host showing a home to visitors',
        summary: 'They show the home\nto future residents.',
        description:
          'The in-person presence for anyone considering the home. They open the door, answer questions on the spot, and lock up safely when the tour ends.',
        // What the best Tour Hosts earn, rather than what the network has paid
        // out: it is the number someone deciding whether to apply is actually
        // asking about, and unlike a cumulative total it does not need
        // restating every quarter. A floor with a `+` rather than a ceiling —
        // it reads as what the role can be worth, not as a cap.
        //
        // The network size and rating are still PLACEHOLDER FIGURES — not
        // sourced, not approved, invented to fill the layout. The ops
        // dashboard tracking tours and inspections by market and contractor
        // type is the source (see #all-reporting_all_teams, 2025-01-24). They
        // must be replaced before this page is promoted past dev1.
        // TODO(EXP-88).
        stats: [
          { label: 'Network', value: '300+ Hosts' },
          { label: 'Top hosts', value: '$3,500+/mo' },
          { label: 'Rating', value: '4.9 out of 5' },
        ],
      },
      {
        title: 'Inspectors',
        imageSrc: `${PRO_ASSETS}/lp_pro_real_img_02.jpg`,
        imageAlt:
          'A Belong Inspector assessing a room against Belong standards',
        summary: 'They hold the home\nto Belong standards.',
        description:
          'The in-person check on a home’s condition at onboarding, move-in, and move-out. Their assessment sets the baseline that repairs, deposits, and damage claims are measured against.',
        // Monthly earnings for the same reason as the Tour Hosts above; the
        // network size and rating are still PLACEHOLDER FIGURES.
        // TODO(EXP-88).
        stats: [
          { label: 'Network', value: '250+ Inspectors' },
          { label: 'Top inspectors', value: '$4,000+/mo' },
          { label: 'Rating', value: '4.8 out of 5' },
        ],
      },
    ],
    /** Every role funnels into the one capture form at the foot of the page.
     *  There is no per-role signup flow yet, so a button that pretended to
     *  start one would be lying about where the lead lands. TODO(EXP-88). */
    applyCta: 'Apply',
    footnote:
      'Three roles, one standard. Every Belong Pro, Tour Host, and Inspector represents Belong in someone\u2019s home: vetted, trained, and held to the same Open Standards, so the experience is consistent no matter who shows up at the door.',
  },
  /** How payment works, then the loop that follows from it. */
  payment: {
    label: 'WHEN YOU GET PAID',
    heading: 'Payment is released when the job is completed.',
    description:
      "Finish the work, close it out on the job's email thread, and payment moves. Nothing to chase and nobody to remind.",
    /** The last step is the payout, and reads as the emphasis of the set. */
    steps: ['Job completed', 'Closed out on the thread', 'Payment released'],
    /** Payment release is live for Pros; the other two roles are not on it
     *  yet. Said plainly here rather than letting a Tour Host read the
     *  section as a promise. */
    scopeNote: '*Pros only for now',
    loopHeading: 'And then it compounds.',
    loopSteps: [
      "You've got\nmail",
      'Reply to\naccept',
      'The visit\nis confirmed',
      'Do the\nwork',
      'Proof of\ncompleted',
      'Get\npaid',
    ],
    /** Marks the step in the loop where the money actually lands. */
    paidBadge: 'PAID HERE',
    paidBadgeStepIndex: 5,
  },
  /** Trades shown as a right-to-left marquee, one photo each.
   *
   *  `paid` is cumulative payouts to Pros in that trade, straight from the
   *  finance rollup. Millions where the figure is one, thousands below that:
   *  "$0.1M" made the smallest trades look like a rounding error rather than
   *  six figures of work.
   *
   *  `and more` carries the sum of every trade without a slide
   *  of its own (Appliances, Flooring, Remediation, Roofing, Carpentry and the
   *  rest), so no work is counted twice across the ring. */
  work: {
    heading: 'The Kind Of Work We Send',
    /** Sits under each trade name in the carousel caption. */
    paidLabel: 'Paid to Pros',
    /** Alt text pattern for the trade photos. The caption already names the
     *  trade, so the alt describes the picture rather than repeating it. */
    imageAltPattern: 'A Belong Pro doing {trade} work',
    trades: [
      {
        label: 'Painting',
        paid: '$2.3M',
        imageSrc: `${PRO_ASSETS}/lp_pro_painting.jpg`,
      },
      {
        label: 'Plumbing',
        paid: '$1.9M',
        imageSrc: `${PRO_ASSETS}/lp_pro_plumbing.jpg`,
      },
      {
        label: 'Cleaning',
        paid: '$1.8M',
        imageSrc: `${PRO_ASSETS}/lp_pro_cleaning.jpg`,
      },
      {
        label: 'HVAC',
        paid: '$1.1M',
        imageSrc: `${PRO_ASSETS}/lp_pro_hvac.jpg`,
      },
      {
        label: 'Electrical',
        paid: '$400K',
        imageSrc: `${PRO_ASSETS}/lp_pro_general_electrician.jpg`,
      },
      {
        label: 'Landscaping',
        paid: '$300K',
        imageSrc: `${PRO_ASSETS}/lp_pro_landscaping.jpg`,
      },
      {
        label: 'Locksmith',
        paid: '$100K',
        imageSrc: `${PRO_ASSETS}/lp_pro_locksmith.jpg`,
      },
      {
        label: 'General Contracting',
        paid: '$2.5M',
        imageSrc: `${PRO_ASSETS}/lp_pro_general_contracting.jpg`,
      },
      {
        label: 'and more',
        paid: '$6.7M',
        imageSrc: `${PRO_ASSETS}/lp_pro_more.jpg`,
      },
    ],
  },
  social: {
    heading: 'Over 2,000 Pros & Counting',
    ratingScore: '4.5 Out Of 5',
    /** Joins the score to its source inside the badge. */
    ratingOn: 'on',
    ratingSource: 'Trustpilot',
    testimonials: [
      {
        imageSrc: `${PRO_ASSETS}/pro_review_img_02.png`,
        quote:
          '"Working with Belong has transformed my business. The payment is always on time, and the homeowners are genuinely appreciative of quality work."',
        author: 'Mike, HVAC Specialist',
        tenure: '3 Years With Belong',
      },
      {
        imageSrc: `${PRO_ASSETS}/pro_review_img_01.png`,
        quote:
          '"I love working with Carla and the team. They\'re the best, and always available for my questions. You can count on me for anything you need!"',
        author: 'John, HVAC Specialist',
        tenure: '4 Years With Belong',
      },
    ],
  },
  faq: {
    heading: 'Frequently Asked Questions',
    items: [
      {
        id: 'become-a-pro',
        question: 'How do I become a Belong Pro?',
        answer:
          'Start by joining the network. We will ask about your business, the trades you cover, and the areas you serve, then walk you through a short onboarding flow. Once your details, tax information, and insurance are on file, you are cleared to take jobs.',
      },
      {
        id: 'onboarding',
        question: 'What does onboarding involve?',
        answer:
          'Seven short steps: a welcome, a training walkthrough, your personal details, your tax information, your insurance, your payment setup, and finally signing your agreement. Most Pros finish in a single sitting.',
      },
      {
        id: 'getting-paid',
        question: 'How and when do I get paid?',
        answer:
          'Submit your completed work through Belong and payment is issued to the account you connected during onboarding. You are not invoicing homeowners directly and you are not waiting on them to pay. Belong handles it.',
      },
      {
        id: 'licensing-and-insurance',
        question: 'What licensing and insurance do I need?',
        answer:
          'Requirements depend on your trade and the states you work in. During the insurance step we will tell you exactly which license and coverage documents to upload for the work you plan to take on.',
      },
      {
        id: 'work-volume',
        question: 'How much work can I expect?',
        answer:
          'Pros in active markets see dozens of jobs a month. Volume depends on your trades, your service area, and how much you choose to take on. You are always the one accepting the job.',
      },
      {
        id: 'get-more-work',
        question: 'How to get more work?',
        answer:
          '**The faster you reply, the more work you get.** Work orders arrive by email, matched to your trade and area, and most go to the first Pro who replies on the thread. No app to check and no bidding.\n\nFrom there, it comes down to how you handle each job. Confirm the window and arrive inside it, and if something changes, tell us at least three hours ahead. Introduce yourself as part of the Belong network at the door. **Send your estimate for approval before you start, since repairs done without prior approval will not be reimbursed. Keep all pricing and payment conversations with Belong, never with residents.**\n\nWhen your window ends, we will email you to check if the work is done. **Reply within one hour with photos of the completed job.** Once the resident confirms the fix, your payment is released within 2 days.\n\n**Every visit is rated.** The Pros who reply fast, communicate clearly, and do it right the first time get more jobs, higher rates, and priority scheduling.',
      },
      {
        id: 'kinds-of-work',
        question: 'What kind of work does Belong send?',
        answer:
          'Maintenance and repairs across a network of rental homes: turn work between residents, scheduled upkeep, and on-call fixes. Belong manages the relationship with the homeowner and the resident so you can focus on the job.',
      },
    ],
  },
  closing: {
    heading: 'You Belong Here',
    description:
      'Thousands of Pros already grow their business with Belong. If you do quality work and want a steady stream of it, we would like to meet you.',
    cta: 'Apply',
  },
} as const;

/** Widens the `as const` literals above into the shape a translation has to
 *  fill: every locale's copy differs, so `'WELCOME TO THE'` as a *type* would
 *  reject the Spanish file rather than type-check it. Readonly is kept, so
 *  neither locale's copy can be mutated at runtime. */
type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends readonly (infer Item)[]
      ? readonly Widen<Item>[]
      : { readonly [Key in keyof T]: Widen<T[Key]> };

/** The contract every locale implements — see `pros.strings.es.ts`. */
export type ProsStrings = Widen<typeof PROS_STRINGS>;
